import type { ReactNode } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import styles from './AppShell.module.css';

export interface AppShellProps {
  children: ReactNode;
  /** `centered` vertically/horizontally centers content (auth screens). */
  variant?: 'default' | 'centered';
  /** Optional banner content; omitted in the centered auth variant. */
  brand?: ReactNode;
}

/**
 * Page frame providing the accessible landmark structure every route inherits:
 * a skip link targeting `#main`, a `role="banner"` header, and a focusable
 * `<main id="main" tabindex="-1">` so post-navigation focus can land on content.
 */
export function AppShell({ children, variant = 'default', brand }: AppShellProps) {
  const brandLogo = (
    <img className={styles.logo} src="/brand/thinktankqa-logo.png" alt="Think Tank QA" />
  );
  return (
    <>
      <a className={styles.skipLink} href="#main">
        Skip to main content
      </a>
      {variant === 'centered' ? (
        <main id="main" tabIndex={-1} className={styles.centered}>
          <div className={styles.centeredInner}>
            <img
              className={styles.centeredLogo}
              src="/brand/thinktankqa-logo.png"
              alt="Think Tank QA"
            />
            {children}
          </div>
        </main>
      ) : (
        <>
          <header role="banner" className={styles.banner}>
            <span className={styles.brand}>{brand ?? brandLogo}</span>
          </header>
          <main id="main" tabIndex={-1} className={styles.main}>
            <Breadcrumbs />
            {children}
          </main>
          <footer className={styles.footer}>
            <span className={styles.footerText}>
              © {new Date().getFullYear()} Think Tank QA. All rights reserved.
            </span>
          </footer>
        </>
      )}
    </>
  );
}
